<template>
    <section class="wrap wrap-huge">
        <div
            class="border border-slate-300 my-14 p-9 at1024:my-23 at1024:p-[72px]"
        >
            <div class="flex flex-col gap-16 at1024:flex-row at1024:gap-0">
                <form
                    id="contact-form"
                    class="at1024:pr-[5%] at1024:w-[70%] at1200:border-r"
                    :class="{ errors: form.hasErrors }"
                >
                    <h1 class="text-3xl at1024:leading-none at1024:text-[80px]">
                        {{ $t("Contact") }}
                    </h1>
                    <p
                        class="leading-tight mb-4 text-lg text-gray-400 at1024:text-xl"
                    >
                        {{
                            $t(
                                "Expect a prompt response to any customer service need."
                            )
                        }}
                    </p>
                    <fieldset :disabled="form.processing">
                        <!--form inputs-->
                        <ul>
                            <li class="py-2">
                                <seclock-input
                                    :animated="true"
                                    :contrast="true"
                                    :error="form.errors.name"
                                    label="Name"
                                    type="text"
                                    required
                                    v-model.trim="form.name"
                                />
                            </li>

                            <li class="py-2">
                                <seclock-input
                                    :animated="true"
                                    :contrast="true"
                                    type="email"
                                    :error="form.errors.email"
                                    label="Email"
                                    required
                                    v-model.trim="form.email"
                                />
                            </li>

                            <li
                                class="flex flex-col w-full at720:gap-4 at720:grid at720:grid-cols-2"
                            >
                                <div class="py-2">
                                    <seclock-input
                                        :animated="true"
                                        :contrast="true"
                                        type="tel"
                                        :error="form.errors.phone"
                                        pattern="^[0-9\s\-\+\(\)\.]{10,17}$"
                                        label="Phone"
                                        v-model.trim="form.phone"
                                        footnoteCharacter="†"
                                    />
                                </div>
                                <div class="py-2">
                                    <seclock-input
                                        type="text"
                                        :animated="true"
                                        :contrast="true"
                                        :error="form.errors.company"
                                        label="Company"
                                        v-model.trim="form.company"
                                    />
                                </div>
                            </li>

                            <li class="py-2">
                                <seclock-input
                                    type="text"
                                    :animated="true"
                                    :contrast="true"
                                    :error="form.errors.subject"
                                    label="Subject"
                                    required
                                    v-model.trim="form.subject"
                                />
                            </li>

                            <li class="py-2">
                                <seclock-input
                                    type="textarea"
                                    :animated="true"
                                    :contrast="true"
                                    :error="form.errors.message"
                                    label="Your message"
                                    rows="6"
                                    required
                                    v-model.trim="form.message"
                                />
                            </li>
                        </ul>
                        <div class="mb-5 mt-3" v-if="user">
                            <p class="mb-3 text-gray-550">
                                <b>{{ $t("Attachment") }}</b> ({{
                                    $t("optional")
                                }})
                            </p>
                            <file-upload
                                :patch_update_route="
                                    route('web.support.contact.upload')
                                "
                                max-file-size="2MB"
                            />
                        </div>
                        <div
                            class="flex flex-col gap-2 at768:flex-row at768:gap-8 at768:items-center"
                        >
                            <button
                                @click.stop.prevent="submit"
                                :disabled="form.processing || !isFormValid"
                                class="btn-white-on-black w-full my-2 at768:shrink-0 at768:w-fit at768:flex justify-center"
                            >
                                <LoadingCircle
                                    v-if="form.processing"
                                    class="p-1"
                                />
                                <span v-else>{{ $t("Send") }}</span>
                            </button>
                            <RecaptchaDisclaimer class="text-gray text-sm" />
                        </div>
                        <p class="mt-7 text-gray-350">† By providing my phone number to SECLOCK I agree and acknowledge that SECLOCK may send text messages to my wireless phone number for any purpose. Message and data rates may apply. We will only send one SMS as a reply to you, and you will be able to Opt-out by replying “STOP”. For more information on how your data will be handled please see our <Link :href="route('web.privacy-policy')" class="underline hover:text-black">Privacy Policy</Link>.</p>
                    </fieldset>
                </form>
                <div
                    class="mx-8 text-gray at1024:m-0 at1024:mt-4 at1024:pl-[60px] at1024:w-[30%] at1200:mt-0 at1200:pt-9"
                >
                    <h4 class="font-semibold">{{ $t("Mailing Address") }}</h4>
                    <p>
                        P.O. Box 95<br />
                        Westwood, MA 02090
                    </p>

                    <h4 class="font-semibold mt-4">
                        {{ $t("Payment Remittance") }}
                    </h4>
                    <p>
                        P.O. Box 847469<br />
                        Boston, MA 02284-7469
                    </p>

                    <p class="font-bold mt-4">
                        <a href="tel:1-800-847-5625">(800) 847-5625</a>
                    </p>
                    <p class="flex flex-row-reverse gap-1 justify-end text-sm">
                        <span class="text-gray-400">{{ $t("fax") }}</span> (800)
                        878-6400
                    </p>
                    <p
                        v-if="user"
                        class="flex flex-row-reverse gap-1 justify-end text-sm"
                    >
                        <span class="text-gray-400">{{ $t("text") }}</span>
                        (833) 344-1103
                    </p>

                    <dl class="mt-4">
                        <template v-for="email in emails">
                            <dt class="font-bold mt-4 first-of-type:mt-0">
                                {{ email.label }}
                            </dt>
                            <dd>
                                <a
                                    :href="`mailto:${email.address.toLowerCase()}`"
                                    class="text-red text-md hover:type-primary hover:underline"
                                    >{{ email.address }}</a
                                >
                            </dd>
                        </template>
                    </dl>
                </div>
            </div>
        </div>
    </section>
    <div class="hidden at1024:block">
        <div class="h-[524px] w-full">
            <google-map
                id="map_usa"
                :markers="mapMarkers"
                :options="config.contact.map.options"
            />
        </div>
        <div class="bg-gray-100 flex justify-center p-16">
            <Link :href="route('web.locations')" class="btn-black-outline-pill"
                >View location details
                <base-svg aria-hidden="true" name="ArrowForward" />
            </Link>
        </div>
    </div>
    <Meta
        title="Commercial Door Lock | Door Lock Wholesaler"
        description="Contact SECLOCK, a commercial door lock wholesaler. Call 800-847-5625 to speak with technical sales experts about door hardware and security solutions."
        :jsonLdMainEntity="jsonLdMainEntityContactPage('Contact SECLOCK', 'Contact SECLOCK, a commercial door lock wholesaler, for technical sales support and door hardware solutions.')"
        :jsonLdBreadcrumbs="[
            { title: 'Home', url: 'https://www.seclock.com' },
            { title: 'Contact', url: 'https://www.seclock.com/contact' }
        ]"
    />
</template>

<script setup>
import config from "@/config";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useForm } from "@inertiajs/vue3";
import {
    BaseSvg,
    FileUpload,
    GoogleMap,
    LoadingCircle,
    Meta,
    RecaptchaDisclaimer,
    SeclockInput,
} from "@/Components";
import { eventBus, useHelpers, useJsonLd, useRecaptcha, useUser } from "@/Composables";

const { scrollToTop } = useHelpers();
const { getRecaptchaToken } = useRecaptcha();
const { jsonLdMainEntityContactPage } = useJsonLd();

const props = defineProps(["user"]);

const { user } = useUser();

const emails = [
    { label: "Email", address: "info@SECLOCK.com" },
    { label: "Orders", address: "orders@SECLOCK.com" },
    { label: "Quotes", address: "quotes@SECLOCK.com" },
    { label: "Credit", address: "credit@SECLOCK.com" },
    { label: "On-Site®️", address: "onsite@SECLOCK.com" },
];

const mapMarkers = computed(() => {
    let markers = {};
    config.locations.forEach((location) => {
        markers[location.id] = location.map.markers[0];
    });
    return markers;
});

const form = useForm({
    name: props.user?.name ?? null,
    email: props.user?.email ?? null,
    phone: props.user?.sms_phone_number?.channel ?? null,
    company: props.user?.team?.name ?? null,
    subject: null,
    message: null,
    docs: [],
    form_name: "contact",
    recaptcha: null,
});

//SLD3-1264 : Attach files to Contact form and subsequently the email notification
const uploaded_files = ref([]);
const onUploadFile = (docs) => (uploaded_files.value = docs);
onMounted(() => eventBus.on("uploadFile:doc", onUploadFile));
onUnmounted(() => eventBus.off("uploadFile:doc", onUploadFile));

const isFormValid = computed(() => {
    // Basic non-empty checks for required fields
    if (!form.name?.trim()) return false;
    if (!form.email?.trim()) return false;

    return true;
});

const submit = async (event) => {
    form.clearErrors();

    //ensure the uploaded and attached documents are included in the form submission.
    form.docs = uploaded_files.value;

    form.recaptcha = await getRecaptchaToken("contact");

    form.post(route("web.sendgrid"), {
        preserveScroll: true,
        onSuccess: () => {
            eventBus.emit("file-list:clear");
            form.reset();
            scrollToTop();
        },
        onError: (response) => {
            let form_errors = Object.keys(response ?? []).filter(
                (key) => typeof key === "string" && key.indexOf("docs") < 0
            );

            if (form_errors.length) {
                eventBus.emit("alert:show", {
                    title: "Error",
                    message: "Required fields are missing.",
                    level: "danger",
                });
            }

            let upload_errors = Object.entries(response ?? [])
                .filter(
                    ([key, message]) =>
                        typeof key === "string" && key.indexOf("docs") > -1
                )
                .map(([key, message]) => message);

            if (upload_errors.length) {
                let unique_error_message = [...new Set(upload_errors)];
                unique_error_message.forEach((error) => {
                    eventBus.emit("alert:show", {
                        title: "Error",
                        message: error,
                        level: "danger",
                    });
                });
            }
        },
    });
};
</script>
